import {
  LogoutUserProfileDocument,
  DetachGroupProfileDocument,
  DetachCharterProfileDocument,
  DetachSalesProfileDocument,
  DetachCouponDocument,
  DetachAllProfilesDocument,
  DetachAllProfilesMutation,
  DetachGroupProfileMutation,
  DetachCharterProfileMutation,
  DetachSalesProfileMutation,
  DetachCouponMutation,
  LogoutUserProfileMutation,
} from "@graphql/api";
import { DocumentNode } from "graphql";
import FrameConfig from "@lib/classes/FrameConfig";
import Cookies from "js-cookie";
import { processJWT } from "@lib/auth/jwt";

type LogoutMutationType = 
  | LogoutUserProfileMutation
  | DetachGroupProfileMutation
  | DetachCharterProfileMutation
  | DetachSalesProfileMutation
  | DetachCouponMutation
  | DetachAllProfilesMutation;

type MutationResponse = {
  success: boolean;
  message?: string | null;
  errorType?: string | null;
  auth?: { token?: string | null } | null;
};

const LOGOUT_MUTATIONS: Record<string, DocumentNode | undefined> = {
  USER: LogoutUserProfileDocument,
  GROUP: DetachGroupProfileDocument,
  CHARTER: DetachCharterProfileDocument,
  SALES: DetachSalesProfileDocument,
  COUPON: DetachCouponDocument,
  ALL: DetachAllProfilesDocument,
};

function extractMutationResponse(data: LogoutMutationType): MutationResponse | null {
  // All mutations follow the same response structure
  if ('logoutUserProfile' in data && data.logoutUserProfile) {
    return data.logoutUserProfile;
  }
  if ('detachGroupProfile' in data && data.detachGroupProfile) {
    return data.detachGroupProfile;
  }
  if ('detachCharterProfile' in data && data.detachCharterProfile) {
    return data.detachCharterProfile;
  }
  if ('detachSalesProfile' in data && data.detachSalesProfile) {
    return data.detachSalesProfile;
  }
  if ('detachCoupon' in data && data.detachCoupon) {
    return data.detachCoupon;
  }
  if ('detachAllProfiles' in data && data.detachAllProfiles) {
    return data.detachAllProfiles;
  }
  
  return null;
}

async function handleLogoutResponse(
  token: string | null | undefined,
  type: string,
  siteId: string
): Promise<void> {
  // For "ALL" logout, completely remove cookies (profiles are read from JWT, so cookie removal is sufficient)
  if (type === "ALL") {
    Cookies.remove(`be_${siteId}`);
    Cookies.remove(`be_${siteId}_e`);
    await window.apolloClient.resetStore();
  } else if (token) {
    // For partial logouts, process the JWT token - this will:
    // 1. Store the token in cookies
    // 2. Reset the Apollo store
    await processJWT(token);
  }
  // If no token is returned for partial logouts, that's ok - just continue

  // Clear group code for GROUP, COUPON, or ALL logouts
  if (type === "GROUP" || type === "ALL" || type === "COUPON") {
    new FrameConfig().update("groupCode", undefined);
  }
}

export default async function logoutProfile(type: string): Promise<void> {
  const client = global.window.apolloClient;
  const siteId = window.siteId;

  // Get the appropriate mutation document
  const mutationDocument = LOGOUT_MUTATIONS[type];
  if (!mutationDocument) {
    console.error(`Unknown logout type: ${type}`);
    return;
  }

  // Execute the mutation
  try {
    const result = await client.mutate<LogoutMutationType>({
      mutation: mutationDocument,
    });

    if (!result.data) {
      console.error("No data in mutation result");
      return;
    }

    // Extract the response from the appropriate field
    const mutationResponse = extractMutationResponse(result.data);
    if (!mutationResponse) {
      console.error("Could not extract mutation response");
      return;
    }

    // Check if the mutation was successful
    if (!mutationResponse.success) {
      console.error(mutationResponse.message || mutationResponse.errorType || "Logout failed");
      return;
    }

    // Handle the logout response - update JWT, session, and cookies
    await handleLogoutResponse(mutationResponse.auth?.token, type, siteId);
  } catch (error) {
    console.error("Logout mutation failed:", error);
    return;
  }
}
